// Data var name should match backend here
/* eslint-disable camelcase */
import { Message, Notification } from 'element-ui';

import api from '../../api';

const state = {
  user: {},
  organization: {},
  profile: {},
  coach_profile: {},
  coach: {},
  cobranding_data: {},
  blur: {
    flag: false,
    option: 'service',
  },
  payment_status: '',
  loading: false,
};

// getters
const getters = {
  hasTimezone: (state) => (!!(state.user && state.user.time_zone)),
  isAuth: (state) => (!!state.user.uuid),
  getUserUUID: (state) => (state.user.uuid),
  getAppcuesData: (state) => ({
    role: state.user.role_type,
  }),
  getTheme: (state) => (state.cobranding_data),
  availEngagements: (state) => {
    if (state.profile.curr_invite_ct >= state.profile.max_invite_ct) {
      return 0;
    }
    return state.profile.max_invite_ct - state.profile.curr_invite_ct;
  },
};

// mutations
const mutations = {
  logoutUser(state) {
    state.user = {};
    state.organization = {};
    state.profile = {};
    state.blur = {
      flag: false,
      option: 'service',
    };
  },

  loginUser(state, {
    user, organization, profile, coach_profile, cobranding_data,
  }) {
    state.user = user;
    state.profile = profile || {};
    state.coach_profile = coach_profile || {};
    state.organization = organization || {};
    state.cobranding_data = cobranding_data || {};
  },

  setTimezone(state, { timezone }) {
    state.time_zone = timezone;
  },

  loadingHandler(state, status) {
    state.loading = status;
  },

  cancellationPolicyAgree(state, status) {
    state.profile.agreed_cancellation_policy = status;
  },

  googleConnectionStatusHandler(state, result) {
    state.user.is_google_oauth = result;
  },

  setBlur(state, data) {
    Object.keys(data).forEach((key) => {
      state.blur[key] = data[key];
    });
  },

  setPaymentStatus(state, data) {
    state.payment_status = data;
  },

  setLeaderDemographics(state, data) {
    state.profile.demographics = data;
  },

  setNewTheme(state, data) {
    let cobranding = {};
    if (data && Object.entries(data).length) {
      cobranding = { ...data };
    }
    state.cobranding_data = cobranding;
  },
};

// actions
const actions = {
  async login({ commit, rootGetters }, credentials) {
    // empty user data
    commit('logoutUser');
    commit('myCoach/removeCoach', null, { root: true });
    commit('loadingHandler', true);

    try {
      const response = await api.login(credentials);
      commit('loginUser', response.data);
      commit('myCoach/setCoach', { coach: response.data.coach }, { root: true });
      commit('common/updateShouldRefreshUser', false, { root: true });

      if (response.data.user.last_login) {
        Notification({
          title: 'Your last login was at: ',
          message: response.data.user.last_login,
          offset: 50,
          iconClass: 'el-icon-bell',
        });
      }

      setTimeout(() => {
        const userData = {
          email: response.data.user.email,
          name: `${response.data.user.first_name} ${response.data.user.last_name}`,
          role: response.data.user.role_type,
          hasCoach: rootGetters['myCoach/hasCoach'],
          currentSession: rootGetters['processStatus/currentSession'],
        };
        // console.log('AppCues', userData);
        window.Appcues.identify(response.data.user.uuid, userData);
      }, 1000);

      // groovehq
      if (response.data.user.role_type === 'leader' || response.data.user.role_type === 'coach') {
        setTimeout(() => {
          if (window.groove) {
            window.groove.widget('setCustomer', {
              email: response.data.user.email,
            });
          }
        }, 1500);
      }

      return response.data;
    } catch (err) {
      if (err.msg) {
        if (err.msg === 'Unable to log in with provided credentials.') {
          Message.error('Your email address and password do not match.');
        } else {
          Message.error(err.msg);
        }
      }
      throw new Error(err.msg);
    } finally {
      commit('loadingHandler', false);
    }
  },

  async fetchCurrentUser({ commit, dispatch }) {
    commit('loadingHandler', true);
    commit('setBlur', { flag: false, option: 'service' });
    try {
      const response = await api.getCurrentUser();
      const { user } = response.data;
      commit('loginUser', response.data);
      const services = response.data.user.services || [];
      if (services && services.length > 0) {
        const gcgFlag = services.some((el) => el.service_id === 'gcg_platform');
        // ************
        // Blur priority top to bottom = 'noService' -> 'Payment' -> 'TOS'
        // ************
        if (!gcgFlag) {
          commit('setBlur', { flag: true });
        } else if (gcgFlag && !response.data.user.is_tos_agreed) {
          commit('setBlur', { flag: true, option: 'tos' });
        }

        dispatch('paymentStatus');
      } else if (process.env.VUE_APP_PLATFORM === 'ING'
          && user.role_type === 'leader') {
        commit('setBlur', { flag: true, option: 'deprecated' });
      } else {
        commit('setBlur', { flag: false });
      }
      commit('common/updateShouldRefreshUser', false, { root: true });
      return response.data;
    } catch (err) {
      if (err.msg
        && ![
          'User is not registered for this service',
          'demographicsIncomplete',
          'user_not_active',
        ].includes(err.msg)) {
        Message.error(err.msg);
      }
      if ([
        'User is not registered for this service',
        'user_not_active',
      ].includes(err.msg)) {
        commit('setBlur', { flag: true, option: 'service' });
      }
      throw err;
    } finally {
      commit('loadingHandler', false);
    }
  },

  async logout({ commit }) {
    // empty all module state data
    try {
      await api.logout();
      commit('common/updateShouldRefreshUser', true, { root: true });
      commit('myCoach/removeCoach', null, { root: true });
      commit('logoutUser');
    } catch (err) {
      console.log(err);
      // we don t want error message for credential on logout
      if (!err.response || err.response.status === 401) {
        if (err.msg) {
          Message.error(err.msg);
        }
      }
      // But keep sequence
      commit('logoutUser');
      commit('myCoach/removeCoach', null, { root: true });
    }
  },

  async policyAgreement({ commit }) {
    try {
      const changeData = {
        profile: {
          agreed_cancellation_policy: true,
        },
      };
      await api.updateUserProfile(changeData);
      commit('cancellationPolicyAgree', true);
    } catch (err) {
      console.log(err);
      if (err.msg) {
        this.$message.error(err.msg);
      }
    }
  },

  async googleConnectionStatus({ commit }, result) {
    if (result) {
      commit('googleConnectionStatusHandler', result);
    }
  },

  async paymentStatus({ commit }) {
    try {
      const response = await api.getPaymentStatus();
      if (response.data.payment_status === 'failed') {
        commit('setBlur', { flag: true, option: 'payment' });
      }
      commit('setPaymentStatus', response.data.payment_status);
    } catch (err) {
      console.log(err);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
